import React, { useEffect, useRef, useState, useCallback } from 'react';
import ItcChartService from '../../services/chart/ItcChartService';
import MarketDataService from '../../services/market/MarketDataService';
import { useIndicators } from '../../hooks/useIndicators';
import { INDICATOR_LIST } from '../../helpers/indicatorConfig';
import { useTheme } from '../../theme/ThemeContext';
import { useSelector } from 'react-redux';
import { ComponentLoader } from '../common/Loading';
import { getOrderVolumeLots, isBuyOrder, getOrderOpenPrice } from '../../services/platform/TradingStore';

// Import modular sub-components for optimized rendering performance and readability
import ChartToolbar from './ChartToolbar';
import ChartHUDOverlay from './ChartHUDOverlay';

const chartServiceRef = { current: null };
export function getChartService() { return chartServiceRef.current; }

const getOrderId = order => order.position || order.position_id || order.id || order._id || order.ticket || order.order;
const toNumber = value => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
};

const TradingChart = ({ symbol, item }) => {
  const theme = useTheme();
  const chartLoading = useSelector(state => state.platform.chartLoading);
  const timeframes = useSelector(state => state.platform.timeframes);
  const chartElRef = useRef(null);
  const svcRef = useRef(null);
  const symbolRef = useRef(symbol);
  const chartTypeRef = useRef('candlestick');

  // Track whether the chart has been fully initialised (has real pixel dimensions)
  const readyRef = useRef(false);
  // Track which symbol was last initialised so we only re-init on actual symbol changes
  const initedSymbolRef = useRef(null);

  const [interval, setInterval] = useState('M1');
  const [chartType, setChartType] = useState('candlestick');
  const [showIndPanel, setShowIndPanel] = useState(false);

  // Chart settings states
  const [showGrid, setShowGrid] = useState(true);
  const [showBidLine, setShowBidLine] = useState(true);
  const [showOrderLines, setShowOrderLines] = useState(true);

  symbolRef.current = symbol;
  chartTypeRef.current = chartType;

  // Zoom callbacks
  const handleZoomIn = useCallback(() => {
    svcRef.current?.zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    svcRef.current?.zoomOut();
  }, []);

  const openOrders = useSelector(state => state.platform.positions.concat(state.platform.pendingOrders));

  // Sync open and pending orders to the chart service as marklines/markpoints
  const syncOrderMarkLines = useCallback(() => {
    const svc = svcRef.current;
    if (!svc || !readyRef.current) return;

    svc.clearMarkLines();
    svc.clearMarkPoints();

    if (!symbol) return;

    const activeOrders = openOrders.filter(
      o => o.symbol?.toLowerCase() === symbol.toLowerCase()
    );

    activeOrders.forEach(order => {
      const isBuy = isBuyOrder(order);
      const typeLabel = isBuy ? 'Buy' : 'Sell';
      const orderId = getOrderId(order);
      const lotSize = getOrderVolumeLots(order).toFixed(2);
      const price = toNumber(getOrderOpenPrice(order));
      const sl = toNumber(order.priceSL ?? order.sl);
      const tp = toNumber(order.priceTP ?? order.tp);

      // 1. Draw Entry Price Line (solid)
      const entryColor = isBuy ? theme.marklineBuy : theme.marklineSell;
      const orderLabel = `${typeLabel} ${lotSize}`;
      svc.addMarkLine(`entry_${orderId}`, {
        name: orderLabel,
        yAxis: price,
        color: entryColor,
        lineType: 'dashed',
        width: 1,
        opacity: 0.62,
        labelPosition: 'end',
        labelDistance: 10,
        labelColor: '#ffffff'
      });

      // 2. Draw Stop Loss (SL) Line (dashed) if set
      if (sl > 0) {
        svc.addMarkLine(`sl_${orderId}`, {
          name: `SL #${orderId}`,
          yAxis: sl,
          color: theme.marklineSL || '#E55604',
          lineType: 'dashed',
          width: 1,
          opacity: 0.58,
          labelPosition: 'end',
          labelDistance: 10,
          labelColor: '#ffffff'
        });
      }

      // 3. Draw Take Profit (TP) Line (dashed) if set
      if (tp > 0) {
        svc.addMarkLine(`tp_${orderId}`, {
          name: `TP #${orderId}`,
          yAxis: tp,
          color: theme.marklineTP || '#bb86fc',
          lineType: 'dashed',
          width: 1,
          opacity: 0.58,
          labelPosition: 'end',
          labelDistance: 10,
          labelColor: '#ffffff'
        });
      }

      // 4. Draw Entry execution bubble (markpoint)
      if (price > 0) {
        svc.addMarkPoint({
          id: `mp_${orderId}`,
          price: price,
          amount: parseFloat(lotSize),
          timestamp: order.timeCreateMsc || order.timeCreate || order.time || order.open_time,
          type: isBuy ? 'buy' : 'sell',
          label: `${isBuy ? 'B' : 'S'} ${lotSize}`
        });
      }
    });
  }, [symbol, openOrders, theme]);

  const syncRef = useRef(null);
  syncRef.current = syncOrderMarkLines;

  // Trigger sync on order, symbol, or theme change
  useEffect(() => {
    if (readyRef.current) {
      syncOrderMarkLines();
    }
  }, [syncOrderMarkLines]);

  // Synchronize settings with ItcChartService
  useEffect(() => {
    const svc = svcRef.current;
    if (!svc) return;

    let changed = false;
    if (svc.showGrid !== showGrid) {
      svc.showGrid = showGrid;
      changed = true;
    }
    if (svc.showBidLine !== showBidLine) {
      svc.showBidLine = showBidLine;
      changed = true;
    }
    if (svc.showOrderLines !== showOrderLines) {
      svc.showOrderLines = showOrderLines;
      changed = true;
    }

    if (changed && readyRef.current) {
      if (svc.showOrderLines) {
        syncOrderMarkLines();
      } else {
        svc.clearMarkLines();
        svc.clearMarkPoints();
      }
      svc._forceRedraw();
    }
  }, [showGrid, showBidLine, showOrderLines, syncOrderMarkLines]);

  // ─── Core init helper ────────────────────────────────────────────────────
  // Lazily creates the service, then calls svc.init().
  // Returns true only when the element has real pixel dimensions.
  const doInit = useCallback((el, sym) => {
    if (!el || !sym) return false;
    if (el.clientWidth === 0 || el.clientHeight === 0) return false;

    if (!svcRef.current) {
      svcRef.current = new ItcChartService();
      chartServiceRef.current = svcRef.current;
      MarketDataService.setChartService(svcRef.current);
    }

    svcRef.current.interval = interval;
    svcRef.current.showGrid = showGrid;
    svcRef.current.showBidLine = showBidLine;
    svcRef.current.showOrderLines = showOrderLines;
    svcRef.current.setThemeColors(theme);
    svcRef.current.init(el, null, sym, chartTypeRef.current);
    initedSymbolRef.current = sym;
    readyRef.current = true;
    
    // Sync open orders immediately upon chart initialization
    syncRef.current?.();
    // Deferred resize to handle flex layout settling after initial paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => { svcRef.current?.resize(); });
    });
    return true;
  }, [interval, showGrid, showBidLine, showOrderLines, theme]);

  // ─── ResizeObserver: first fire inits, subsequent fires resize ──────────
  useEffect(() => {
    const el = chartElRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      if (!readyRef.current) {
        // Not yet initialised — try now (el may finally have dimensions)
        readyRef.current = doInit(el, symbolRef.current);
      } else {
        svcRef.current?.resize();
      }
    });
    ro.observe(el);

    // Try immediately — succeeds if the element already has dimensions on mount
    if (!readyRef.current) {
      readyRef.current = doInit(el, symbolRef.current);
      if (readyRef.current) {
        // Succeeded immediately; record which symbol was inited so the
        // symbol-change effect below does NOT duplicate the call.
        initedSymbolRef.current = symbolRef.current;
      }
    }

    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Symbol change: re-init only when the symbol actually changes ────────
  useEffect(() => {
    if (!symbol) return;

    // If the ResizeObserver already inited this exact symbol, skip.
    if (initedSymbolRef.current === symbol) return;

    // If not ready, try to initialize now (e.g. symbol just loaded)
    if (!readyRef.current) {
      const el = chartElRef.current;
      if (el && el.clientWidth > 0 && el.clientHeight > 0) {
        readyRef.current = doInit(el, symbol);
        if (readyRef.current) return;
      }
    }

    // Service not yet created means ResizeObserver hasn't fired yet with real
    // dimensions — it will init with the correct symbol when it does.
    if (!svcRef.current || !readyRef.current) return;

    svcRef.current.setThemeColors(theme);
    svcRef.current.init(chartElRef.current, null, symbol, chartTypeRef.current);
    initedSymbolRef.current = symbol;
    syncRef.current?.();
    indicatorControls.resetIndicators();

    return () => { svcRef.current?.reset(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, theme, doInit]);

  // ─── Theme sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (svcRef.current) svcRef.current.setThemeColors(theme);
  }, [theme]);

  // ─── Interval change ─────────────────────────────────────────────────────
  const handleInterval = useCallback(iv => {
    setInterval(iv);
    svcRef.current?.candleSizeInterval(iv);
    indicatorControls.resetIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Chart type change ────────────────────────────────────────────────────
  const handleChartType = useCallback(ct => {
    setChartType(ct);
    svcRef.current?.changeChartType(ct);
  }, []);

  const indicatorControls = useIndicators(svcRef.current);

  const groupedInds = INDICATOR_LIST.reduce((acc, ind) => {
    if (!acc[ind.group]) acc[ind.group] = [];
    acc[ind.group].push(ind);
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: theme.chartBg }}>
      {/* Toolbar */}
      <ChartToolbar
        theme={theme}
        interval={interval}
        chartType={chartType}
        showIndPanel={showIndPanel}
        setShowIndPanel={setShowIndPanel}
        handleInterval={handleInterval}
        handleChartType={handleChartType}
        indicatorControls={indicatorControls}
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        showGrid={showGrid}
        setShowGrid={setShowGrid}
        showBidLine={showBidLine}
        setShowBidLine={setShowBidLine}
        showOrderLines={showOrderLines}
        setShowOrderLines={setShowOrderLines}
        groupedInds={groupedInds}
        timeframes={timeframes}
      />

      {/* Fixed chart header */}
      <ChartHUDOverlay
        symbol={symbol}
        item={item}
        theme={theme}
      />

      {/* Chart canvas */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
        {chartLoading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: theme.chartBg + 'AA', zIndex: 10, backdropFilter: 'blur(2px)' }}>
            <ComponentLoader message={`Loading ${symbol} chart...`} />
          </div>
        )}
        <div ref={chartElRef} style={{ width: '100%', height: '100%' }} />
      </div>
    </div>
  );
};

export default TradingChart;
